import * as React from 'react';
import { cn } from './lib/utils';
import { EmptyState } from './EmptyState';

const RATIOS = {
  '1:1': { grid: 'lg:grid-cols-2', left: 'lg:col-span-1', right: 'lg:col-span-1' },
  '2:3': { grid: 'lg:grid-cols-5', left: 'lg:col-span-2', right: 'lg:col-span-3' },
  '3:2': { grid: 'lg:grid-cols-5', left: 'lg:col-span-3', right: 'lg:col-span-2' },
  '1:2': { grid: 'lg:grid-cols-3', left: 'lg:col-span-1', right: 'lg:col-span-2' },
  '2:1': { grid: 'lg:grid-cols-3', left: 'lg:col-span-2', right: 'lg:col-span-1' },
};

/**
 * Two-panel layout: scrollable list on left, detail on right.
 * Implements §14c of the design system.
 *
 * @param {object} props
 * @param {"1:1"|"2:3"|"3:2"|"1:2"|"2:1"} [props.ratio="2:3"]
 * @param {React.ReactNode} [props.listHeader] — renders above the scrollable list (shrink-0)
 * @param {React.ReactNode} props.list — list items
 * @param {React.ReactNode} [props.emptyList] — shown when list is empty
 * @param {React.ReactNode} [props.detail] — detail panel content (null = show empty state)
 * @param {string} [props.emptyDetail="Select an item"] — empty detail placeholder text
 * @param {React.ReactNode} [props.listFooter] — footer below list (shrink-0)
 * @param {React.ReactNode} [props.detailHeader] — fixed header above detail scroll area
 * @param {React.ReactNode} [props.detailFooter] — fixed footer below detail scroll area
 * @param {string} [props.className]
 */
function MasterDetailLayout({
  ratio = '2:3',
  listHeader,
  list,
  emptyList,
  detail,
  emptyDetail = 'Select an item',
  listFooter,
  detailHeader,
  detailFooter,
  className,
}) {
  const r = RATIOS[ratio] ?? RATIOS['2:3'];
  const hasListContent = React.Children.count(list) > 0;

  return (
    <div className={cn('grid grid-cols-1 gap-2 flex-1 min-h-0', r.grid, className)}>
      {/* Left: list panel */}
      <div className={cn(
        'flex flex-col rounded-lg border border-nn-border bg-nn-white overflow-hidden min-w-0',
        r.left
      )}>
        {listHeader && (
          <div className="shrink-0 p-1.5 border-b border-nn-border">
            {listHeader}
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
          {hasListContent ? list : emptyList}
        </div>
        {listFooter && (
          <div className="shrink-0 border-t border-nn-border p-1.5">
            {listFooter}
          </div>
        )}
      </div>

      {/* Right: detail panel */}
      <div className={cn(
        'flex flex-col rounded-lg border border-nn-border bg-nn-white overflow-hidden min-w-0',
        r.right
      )}>
        {detail ? (
          <>
            {detailHeader && (
              <div className="shrink-0 px-4 py-3 border-b border-nn-border">
                {detailHeader}
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
              {detail}
            </div>
            {detailFooter && (
              <div className="shrink-0 px-4 py-3 border-t border-nn-border">
                {detailFooter}
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <EmptyState title={emptyDetail} size="md" />
          </div>
        )}
      </div>
    </div>
  );
}

export { MasterDetailLayout };
